import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import chartOfAccountsAPI from "../../services/chartOfAccountsAPI";

const natureColor = (nature: string) => {
  switch (String(nature).toUpperCase()) {
    case "ASSET":
      return "success";
    case "LIABILITY":
      return "warning";
    case "INCOME":
      return "info";
    case "EXPENSE":
      return "error";
    case "EQUITY":
      return "secondary";
    default:
      return "default";
  }
};

const ChartOfAccountsPage: React.FC = () => {
  const { data = [], isLoading, error } = useQuery({
    queryKey: ["chart-of-accounts"],
    queryFn: () => chartOfAccountsAPI.list(),
  });

  return (
    <Box>
      <PageHeader
        title="Chart of accounts"
        subtitle="Default India SMB ledgers (foundation — journals coming next)"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Reports", to: "/reports" },
          { label: "Chart of accounts" },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || "Failed to load chart of accounts"}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {data.map((group) => (
            <Paper key={group.id} variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  {group.name}
                </Typography>
                <Chip
                  size="small"
                  label={group.nature}
                  color={natureColor(group.nature) as any}
                />
                <Typography variant="caption" color="text.secondary">
                  {group.code} · {group.ledgers?.length || 0} ledgers
                </Typography>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={120}>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell width={100}>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(group.ledgers || []).map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>
                        <Typography fontFamily="monospace" fontSize={13}>
                          {l.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{l.name}</TableCell>
                      <TableCell>
                        {l.isSystem ? (
                          <Chip size="small" label="System" variant="outlined" />
                        ) : (
                          <Chip size="small" label="Custom" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(group.ledgers || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography color="text.secondary">
                          No ledgers in this group
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          ))}
          {data.length === 0 && (
            <Typography color="text.secondary">
              No ledger groups yet. Run backend migrations.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ChartOfAccountsPage;
